/* ============================================================
   Peachy Gut Check — quiz funnel engine.
   Self-contained vanilla JS. Config (offer %, destination URL,
   logo, product image, proof image) is injected by
   sections/peachy-quiz.liquid via #peachy-quiz-config. Copy
   lives here, like the reference funnel this is modeled on, so
   marketing edits are one file.

   Flow: opening (headline + Q1 + review) → path questions →
   proof break → more questions → analyzing → email gate →
   persona result.

   Paths (set by Q1): "prep" (bottom/vers), "gut" (gut stuff),
   "top". Top and gut share screens; prompts swap per path.

   QA: ?quiz_reset=1 clears saved state.
       ?quiz_persona_preview=<slug> jumps straight to that
       persona's result screen.
   ============================================================ */
(function () {
  'use strict';

  var mount = document.getElementById('peachy-quiz');
  if (!mount) return;

  var cfg = {};
  try {
    cfg = JSON.parse(document.getElementById('peachy-quiz-config').textContent);
  } catch (e) { /* defaults below */ }
  var OFFER_PCT = cfg.offerPct || 38;
  var DEST_URL = cfg.destUrl || '/products/peachy-clean';
  var PROOF_IMG = cfg.proofImg || '';
  var LOGO_URL = cfg.logoUrl || '';
  var REVIEW_PHOTO = cfg.reviewPhoto || '';
  var REVIEW_QUOTE = cfg.reviewQuote || '';
  var REVIEW_AUTHOR = cfg.reviewAuthor || '';
  var PRODUCT_IMG = cfg.productImg || '';
  var STORE_KEY = 'peachy_gut_check_v2';

  /* ----------------------------------------------------------
     Content
     ---------------------------------------------------------- */

  // Question prompts that differ by path use {prep:..., gut:..., top:...}.
  // Options may carry `paths` to limit which paths see them.
  var SCREENS = [
    {
      id: 'position', type: 'single', opening: true,
      kicker: 'The Peachy Gut Check',
      heading: 'Find out what’s happening in your gut that’s killing your confidence in the bedroom',
      check: 'This 2-minute assessment analyzes your symptoms, routine, and habits in order to find a solution.',
      skip: 'Skip the quiz. Shop Peachy Clean',
      review: {
        quote: 'I’ve been cutting down bathroom time before the bedroom like crazy! Douching used to be the worst two hours, now it’s like 5 minutes, 10 max. Bottoming made easy and I’m super confident.',
        author: 'Robert Phillips, Verified Customer'
      },
      prompt: 'First things first. In bed, you’re usually...',
      options: [
        { value: 'bottom', label: 'Bottom', emoji: '🍑' },
        { value: 'vers', label: 'Vers', emoji: '🔄' },
        { value: 'top', label: 'Top', emoji: '🍆' },
        { value: 'gut', label: 'Just here for the gut stuff', emoji: '🧠' }
      ]
    },
    {
      id: 'concern', type: 'single',
      prompt: {
        prep: 'What’s the biggest thing you want to fix right now?',
        gut: 'What’s the biggest thing you want to fix right now?',
        top: 'What brings you here today?'
      },
      options: [
        { value: 'prep-time', label: 'Prep takes way too long (30+ minutes)', emoji: '⏳', paths: ['prep'] },
        { value: 'trust', label: 'I don’t feel fully confident in the bedroom', emoji: '😰', paths: ['prep'] },
        { value: 'spontaneity', label: 'I want more spontaneous sex', emoji: '🔥', paths: ['prep'] },
        { value: 'bloating', label: 'Bloating and an unpredictable gut', emoji: '🎈' },
        { value: 'regularity', label: 'Incomplete, irregular trips', emoji: '🚽', paths: ['gut', 'top'] },
        { value: 'partner', label: 'Something for my partner', emoji: '🎁', paths: ['top'] },
        { value: 'curious', label: 'I’m curious about bottoming', emoji: '👀', paths: ['top'] }
      ]
    },
    {
      id: 'routine', type: 'single',
      prompt: {
        prep: 'What’s your current douching routine?',
        gut: 'How do you keep your gut regular right now?',
        top: 'How do you keep your gut in check right now?'
      },
      options: [
        { value: 'hose', label: 'Shower hose, deep clean, every time', emoji: '🚿', paths: ['prep'] },
        { value: 'full-douche', label: 'Full douche, every time', emoji: '💧', paths: ['prep'] },
        { value: 'quick-rinse', label: 'Quick rinse and hope', emoji: '🤞', paths: ['prep'] },
        { value: 'fiber-plus', label: 'Fiber supplement plus douching', emoji: '💊', paths: ['prep'] },
        { value: 'fiber', label: 'A fiber supplement or powder', emoji: '💊', paths: ['gut', 'top'] },
        { value: 'diet', label: 'I just try to eat right', emoji: '🥗', paths: ['gut', 'top'] },
        { value: 'nothing', label: 'Nothing consistent', emoji: '🤷' }
      ]
    },
    {
      id: 'poops', type: 'single',
      prompt: 'How would you describe your trips to the bathroom lately?',
      options: [
        { value: 'clean', label: 'Clean, complete, regular', emoji: '✅' },
        { value: 'unpredictable', label: 'Fine, but unpredictable', emoji: '🎲' },
        { value: 'incomplete', label: 'Often feels incomplete', emoji: '😕' },
        { value: 'chaos', label: 'All over the place', emoji: '🌪️' }
      ]
    },
    {
      id: 'burden', type: 'single',
      prompt: {
        prep: 'How long does prep actually take you?',
        gut: 'How often does your gut feel bloated or heavy?',
        top: 'How often does your gut feel bloated or heavy?'
      },
      options: [
        { value: 'long', emoji: '😩', label: { prep: '45 minutes or more', gut: 'Most days', top: 'Most days' } },
        { value: 'medium', emoji: '⏱️', label: { prep: '20 to 45 minutes', gut: 'A few times a week', top: 'A few times a week' } },
        { value: 'short', emoji: '⚡', label: { prep: 'Under 20 minutes', gut: 'Once in a while', top: 'Once in a while' } },
        { value: 'avoid', emoji: '🙈', label: { prep: 'Honestly, I sometimes avoid douching entirely', gut: 'Rarely or never', top: 'Rarely or never' } }
      ]
    },
    {
      id: 'trust', type: 'single',
      prompt: {
        prep: 'Be honest. After douching, how confident do you actually feel?',
        gut: 'How much do you trust your gut day to day?',
        top: 'How much do you trust your gut day to day?'
      },
      options: [
        { value: 'confident', emoji: '💪', label: { prep: 'Totally confident', gut: 'It’s totally predictable', top: 'It’s totally predictable' } },
        { value: 'brace', emoji: '😬', label: { prep: 'Mostly fine, but I still brace a little', gut: 'Mostly fine, with off days', top: 'Mostly fine, with off days' } },
        { value: 'fifty', emoji: '🎲', label: { prep: '50-50. I’ve been caught off guard', gut: '50-50. It surprises me', top: '50-50. It surprises me' } },
        { value: 'never-sure', emoji: '🤷', label: { prep: 'I’m never really sure', gut: 'I never really know', top: 'I never really know' } }
      ]
    },
    {
      id: 'proof', type: 'proof',
      heading: 'You’re in the right place.',
      sub: {
        prep: 'Over 100,000 bottoms have ditched the douching guesswork with Peachy Clean. A few more questions and we’ll show you exactly what’s working against you.',
        gut: 'Over 100,000 men have fixed the gut guesswork with Peachy Clean. A few more questions and we’ll show you exactly what’s working against you.',
        top: 'Over 100,000 men have fixed the gut guesswork with Peachy Clean. A few more questions and we’ll show you exactly what’s working against you.'
      },
      cta: 'Keep going'
    },
    {
      id: 'cost', type: 'single',
      prompt: {
        prep: 'Ever turned down sex because you weren’t 100% sure you were clean?',
        gut: 'How often does your gut change your plans?',
        top: 'How often does your gut change your plans?'
      },
      options: [
        { value: 'often', emoji: '😔', label: { prep: 'More times than I’d like to admit', gut: 'All the time', top: 'All the time' } },
        { value: 'few', emoji: '😕', label: { prep: 'A few times', gut: 'A few times a month', top: 'A few times a month' } },
        { value: 'rare', emoji: '😐', label: { prep: 'Once or twice', gut: 'Rarely', top: 'Rarely' } },
        { value: 'never', emoji: '😎', label: { prep: 'Never', gut: 'Never', top: 'Never' } }
      ]
    },
    {
      id: 'tried', type: 'single',
      prompt: 'Have you tried fiber supplements for this before?',
      options: [
        { value: 'current', label: 'Taking one right now', emoji: '💊' },
        { value: 'quit', label: 'Tried one, didn’t stick', emoji: '🗑️' },
        { value: 'powders', label: 'Tried powders like Metamucil', emoji: '🥤' },
        { value: 'never', label: 'Never tried fiber', emoji: '🆕' }
      ]
    },
    {
      id: 'meals', type: 'single',
      prompt: {
        prep: 'Do you eat differently when you know sex is on the menu?',
        gut: 'Do you plan meals around how your gut might react?',
        top: 'Do you plan meals around how your gut might react?'
      },
      options: [
        { value: 'fast', emoji: '🚫', label: { prep: 'I basically stop eating', gut: 'Constantly, it runs my menu', top: 'Constantly, it runs my menu' } },
        { value: 'skip', emoji: '🌶️', label: { prep: 'I skip the risky stuff', gut: 'I avoid the risky stuff', top: 'I avoid the risky stuff' } },
        { value: 'sometimes', emoji: '🤔', label: 'Sometimes, if I remember' },
        { value: 'no', emoji: '🍽️', label: 'No, I eat normally' }
      ],
      why: {
        heading: 'Why we ask',
        body: {
          prep: 'Skipping meals before sex is one of the most common workarounds we hear. It’s also a sign your gut, not your douching, is the real problem.',
          gut: 'Planning your life around your gut is one of the most common signs the underlying rhythm is off, not the food.',
          top: 'Planning your life around your gut is one of the most common signs the underlying rhythm is off, not the food.'
        }
      }
    },
    {
      id: 'desire', type: 'single',
      prompt: 'What would change your life most?',
      options: [
        { value: 'ready', emoji: '❤️‍🔥', label: 'Sex on zero notice, whenever he is ready', paths: ['prep'] },
        { value: 'fast-prep', emoji: '⚡', label: 'Prep done in 5 minutes, not 45', paths: ['prep'] },
        { value: 'confidence', emoji: '😎', label: 'Never second-guessing myself mid-sex', paths: ['prep'] },
        { value: 'bottom-more', emoji: '✨', label: 'Bottoming more often (or for the first time)', paths: ['prep', 'top'] },
        { value: 'calm-gut', emoji: '🧘', label: 'A calm, predictable gut' },
        { value: 'regular', emoji: '🕐', label: 'Same time, every time, no drama', paths: ['gut', 'top'] },
        { value: 'all', emoji: '🍑', label: 'All of the above' }
      ]
    },
    {
      id: 'analyzing', type: 'analyzing',
      heading: 'Building your profile…',
      sub: 'Hold tight while we map your answers against the Peachy Clean gut framework.',
      footnote: 'Your result is almost ready',
      steps: [
        { icon: 'chart', label: 'Analyzing your symptoms' },
        { icon: 'loop', label: 'Cross-referencing your routine' },
        { icon: 'gauge', label: 'Calibrating your protocol' },
        { icon: 'sparkle', label: 'Personalizing your result' }
      ]
    },
    { id: 'result', type: 'result' }
  ];

  /* Persona result content. Beats follow the ad-proven structure:
     reframe → data → empathy → mechanism. */
  var PERSONAS = {
    'ritual-prepper': {
      title: 'The Ritual Prepper',
      headline: 'Your douching routine is doing the wrong job.',
      beats: [
        { h: 'The real issue', p: 'You’ve been douching wrong. Not the technique: the location. A douche reaches about 15cm. Your colon is around 150cm. The part that actually causes problems sits way upstream, and no amount of douching touches it.' },
        { h: 'Why it never feels finished', p: 'That’s why you can prep perfectly and still not trust yourself an hour later. The problem was never where you were looking.' },
        { h: 'What actually works', p: 'Specific gut bacteria, fed the right prebiotic fiber, produce a compound called butyrate. Butyrate regulates how your colon contracts. Complete emptying, predictable and reliable, before douching is ever your problem.' },
        { h: 'Your protocol', p: 'Peachy Clean is prebiotic fiber plus a spore-forming probiotic, designed to feed the bacteria that produce butyrate. Two gummies a day. Your gut does the prep upstream, so you barely have to.' }
      ]
    },
    'over-doucher': {
      title: 'The Deep Cleaner',
      headline: 'You’re not under-prepping. You may be over-douching.',
      beats: [
        { h: 'The real issue', p: 'The deep clean feels thorough, but flooding your colon on repeat washes out the good bacteria your gut depends on and can irritate the lining. The harder you clean, the more unpredictable things can get. You’ve felt that loop.' },
        { h: 'And here’s the kicker', p: 'Even the deepest rinse reaches about 15cm. Your colon is around 150cm. The part that actually causes problems sits way upstream, so the extra effort was never reaching it anyway.' },
        { h: 'What actually works', p: 'Specific gut bacteria, fed the right prebiotic fiber, produce a compound called butyrate. Butyrate regulates how your colon contracts. Complete emptying, predictable and reliable, so the deep clean stops being necessary.' },
        { h: 'Your protocol', p: 'Peachy Clean is prebiotic fiber plus a spore-forming probiotic, built to refeed and restore the bacteria doing the real work upstream. Two gummies a day, and the hose can retire.' }
      ]
    },
    'spontaneity-seeker': {
      title: 'The Spontaneity Seeker',
      headline: 'The goal isn’t better douching. It’s needing less of it.',
      beats: [
        { h: 'The real cost', p: 'Add it up: the minutes, the planning, the times you said no because you weren’t sure. Douching isn’t just a routine, it’s a tax on your sex life.' },
        { h: 'Why douching can’t solve it', p: 'A douche reaches about 15cm of a 150cm colon. As long as “ready” depends on a 30-minute ritual, you’ll never feel ready on no notice. The fix has to happen before douching even starts.' },
        { h: 'What actually works', p: 'When the right gut bacteria are fed the right prebiotic fiber, they produce butyrate, the compound that regulates how your colon empties. Complete, predictable, on your body’s schedule. That’s what makes zero-notice actually possible.' },
        { h: 'Your protocol', p: 'Peachy Clean is prebiotic fiber plus a spore-forming probiotic. Two gummies a day, and your gut stays a step ahead of your sex life instead of behind it.' }
      ]
    },
    'switcher': {
      title: 'The Switcher',
      headline: 'The fiber you tried was never built for this.',
      beats: [
        { h: 'The real issue', p: 'You’ve been taking fiber wrong. Not the dose: the type. Insoluble fiber, the kind in most powders and competitor gummies, just adds bulk. It doesn’t change what’s happening upstream.' },
        { h: 'Why it let you down', p: 'That’s why you could take it every day and still not trust the result. Bulk without regulation is just more traffic on the same broken road.' },
        { h: 'What’s different here', p: 'The root cause is your microbiome. Specific gut bacteria, fed the right prebiotic fiber, produce butyrate, the compound that regulates how your colon contracts. Complete emptying, predictable and reliable. Different mechanism, not a bigger dose.' },
        { h: 'Your protocol', p: 'Peachy Clean pairs prebiotic fiber with a spore-forming probiotic, designed specifically to feed your butyrate-producing bacteria. It does the job the bulk fiber never could.' }
      ]
    },
    'gut-first': {
      title: 'The Gut-First Guy',
      headline: 'Your gut is missing the signal that keeps you regular.',
      beats: [
        { h: 'The real issue', p: 'Unpredictable, incomplete bathroom trips aren’t random. Your colon relies on a chemical signal to tell it when to empty and how completely. When that signal is weak, your body guesses: sometimes too early, sometimes not enough, sometimes twice in an hour.' },
        { h: 'Where the signal comes from', p: 'That signal is a compound called butyrate. Specific gut bacteria make it when they’re fed the right prebiotic fiber. Feed those bacteria and you empty out fully, at the same time each day. Starve them and you get exactly the unpredictability you just described.' },
        { h: 'Why more fiber alone won’t fix it', p: 'Insoluble fiber, the kind in most powders, just adds bulk for your colon to push. If the butyrate-making bacteria aren’t being fed, more bulk means more work with the same weak signal.' },
        { h: 'Your protocol', p: 'Peachy Clean is prebiotic fiber plus a spore-forming probiotic, built to feed your butyrate producers. Two gummies a day for complete, predictable bathroom trips. A debloated waistline is a nice side effect.' }
      ]
    },
    'first-timer': {
      title: 'The Explorer',
      headline: 'The only thing between you and bottoming more is trust in your gut.',
      beats: [
        { h: 'The real barrier', p: 'Wanting to bottom more, or for the first time, and actually doing it are separated by one thing: knowing your body won’t surprise you. That’s not a technique problem or an experience problem. It’s a trust problem.' },
        { h: 'Why douching doesn’t build trust', p: 'The standard advice is “just douche more.” But a douche reaches about 15cm and your colon is around 150cm. The part that causes surprises sits way upstream, which is why even guys who prep hard still feel unsure. More rinsing never buys real certainty.' },
        { h: 'What builds it', p: 'Certainty comes from your gut emptying completely and predictably on its own. Specific gut bacteria, fed the right prebiotic fiber, produce butyrate, the compound that makes that happen. When you know you’re clean before prep even starts, trying something new stops feeling like a gamble.' },
        { h: 'Your protocol', p: 'Peachy Clean is prebiotic fiber plus a spore-forming probiotic. Two gummies a day, whether it’s for you or for the partner you’d love to see more confident.' }
      ]
    },
    'optimizer': {
      title: 'The Optimizer',
      headline: 'Things mostly work. Here’s how you keep it that way.',
      beats: [
        { h: 'Where you stand', p: 'Your answers show fewer red flags than most guys who take this. That’s worth protecting, because gut rhythm is a maintenance game, not a one-time fix.' },
        { h: 'The part most guys never get told', p: 'Regularity runs on butyrate, a compound made by gut bacteria when they’re fed the right prebiotic fiber. Keep those bacteria fed and your colon keeps its schedule. Starve them and the guessing starts.' },
        { h: 'Daily beats situational', p: 'Situational fixes, douching included, only reach the last 15cm of a 150cm system. A daily gummy works the whole system, upstream, before anything is ever your problem.' },
        { h: 'Your protocol', p: 'Peachy Clean is prebiotic fiber plus a spore-forming probiotic. Two gummies a day to keep what works working. And if someone you know spends 30 minutes douching, this is the thing they wish existed.' }
      ]
    }
  };

  /* Answer chips shown on the result: value → short label. */
  var CHIP_LABELS = {
    'routine:hose': { prep: 'Deep-clean doucher', gut: null },
    'burden:long': { prep: '45+ min prep', gut: 'Bloated most days', top: 'Bloated most days' },
    'burden:medium': { prep: '20–45 min prep', gut: 'Bloated weekly', top: 'Bloated weekly' },
    'burden:avoid': { prep: 'Sometimes avoids douching', gut: null, top: null },
    'trust:brace': { prep: 'Still braces after douching', gut: 'Off days', top: 'Off days' },
    'trust:fifty': { prep: 'Caught off guard before', gut: 'Gut surprises him', top: 'Gut surprises him' },
    'trust:never-sure': { prep: 'Never really sure', gut: 'Never really knows', top: 'Never really knows' },
    'cost:often': { prep: 'Said no to sex before', gut: 'Gut changes plans', top: 'Gut changes plans' },
    'cost:few': { prep: 'Missed chances before', gut: 'Plans disrupted monthly', top: 'Plans disrupted monthly' },
    'tried:current': { all: 'On fiber now' },
    'tried:quit': { all: 'Fiber didn’t stick' },
    'tried:powders': { all: 'Tried the powders' },
    'meals:fast': { prep: 'Skips meals before sex', gut: 'Gut runs the menu', top: 'Gut runs the menu' },
    'meals:skip': { prep: 'Eats around sex', gut: 'Avoids risky foods', top: 'Avoids risky foods' },
    'poops:unpredictable': { all: 'Unpredictable bathroom trips' },
    'poops:incomplete': { all: 'Incomplete bathroom trips' },
    'poops:chaos': { all: 'No rhythm at all' }
  };

  var TIMELINE = [
    { when: 'Hour 48', what: 'Most guys see the first results: cleaner, more complete bathroom trips.' },
    { when: 'Day 3–4', what: 'Your gut starts finding a rhythm. Less bloat, more predictability.' },
    { when: 'Day 7', what: 'The new normal: complete, regular, reliable. Douching gets shorter or disappears.' }
  ];

  /* ----------------------------------------------------------
     Translations. sections/peachy-quiz.liquid emits
     window.peachyQuizES (a flat key → string map) for Spanish
     visitors only. Only DISPLAY text is swapped: answer values,
     persona slugs, paths and the Klaviyo / Meta payloads stay
     English. With no map present every T() call returns the
     English argument and localizeContent() never runs, so the
     English quiz is untouched. Any missing key falls back to
     English.
     ---------------------------------------------------------- */
  var L10N = (window.peachyQuizES && typeof window.peachyQuizES === 'object') ? window.peachyQuizES : null;

  function T(key, en) {
    if (!L10N) return en;
    var v = L10N[key];
    return (typeof v === 'string' && v !== '') ? v : en;
  }

  function slug(v) { return String(v).replace(/[^a-zA-Z0-9]+/g, '_'); }

  // String → T(key); per-path object → each non-null entry via T(key.path).
  function trField(v, key) {
    if (v == null) return v;
    if (typeof v === 'string') return T(key, v);
    var out = {};
    Object.keys(v).forEach(function (p) {
      out[p] = v[p] == null ? v[p] : T(key + '.' + p, v[p]);
    });
    return out;
  }

  function localizeContent() {
    SCREENS.forEach(function (s) {
      var b = 's_' + slug(s.id);
      ['kicker', 'heading', 'check', 'skip', 'prompt', 'sub', 'cta', 'footnote'].forEach(function (f) {
        if (s[f] != null) s[f] = trField(s[f], b + '.' + f);
      });
      if (s.review) {
        s.review = { quote: T(b + '.review_quote', s.review.quote), author: T(b + '.review_author', s.review.author) };
      }
      if (s.options) {
        s.options.forEach(function (o) { o.label = trField(o.label, b + '.opt_' + slug(o.value)); });
      }
      if (s.why) {
        s.why = { heading: T(b + '.why_heading', s.why.heading), body: trField(s.why.body, b + '.why_body') };
      }
      if (s.steps) {
        s.steps.forEach(function (st) { st.label = T(b + '.step_' + slug(st.icon), st.label); });
      }
    });
    Object.keys(PERSONAS).forEach(function (id) {
      var P = PERSONAS[id];
      var b = 'p_' + slug(id);
      P.title = T(b + '.title', P.title);
      P.headline = T(b + '.headline', P.headline);
      P.beats.forEach(function (bt, i) {
        bt.h = T(b + '.b' + (i + 1) + '_h', bt.h);
        bt.p = T(b + '.b' + (i + 1) + '_p', bt.p);
      });
    });
    Object.keys(CHIP_LABELS).forEach(function (k) {
      CHIP_LABELS[k] = trField(CHIP_LABELS[k], 'chip_' + slug(k));
    });
    TIMELINE.forEach(function (row, i) {
      row.when = T('tl_' + (i + 1) + '.when', row.when);
      row.what = T('tl_' + (i + 1) + '.what', row.what);
    });
  }

  if (L10N) {
    try { localizeContent(); } catch (e) { /* keep English on any mismatch */ }
  }

  /* ----------------------------------------------------------
     State
     ---------------------------------------------------------- */
  var state = { idx: 0, answers: {}, email: null, done: false };
  var personaPreview = null;

  try {
    var qp = new URLSearchParams(location.search);
    personaPreview = qp.get('quiz_persona_preview');
    if (personaPreview && !PERSONAS[personaPreview]) personaPreview = null;
    if (qp.get('quiz_reset') === '1') {
      localStorage.removeItem(STORE_KEY);
    } else if (!personaPreview) {
      var saved = JSON.parse(localStorage.getItem(STORE_KEY));
      if (saved && typeof saved.idx === 'number' && saved.answers) {
        state = saved;
        state.idx = Math.max(0, Math.min(state.idx, SCREENS.length - 1));
        if (state.persona && !PERSONAS[state.persona]) state.persona = null;
      }
    }
  } catch (e) { /* fresh state */ }

  function persist() {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(state)); } catch (e) { /* private mode */ }
  }

  function path() {
    var p = state.answers.position;
    if (p === 'bottom' || p === 'vers') return 'prep';
    if (p === 'top') return 'top';
    return 'gut';
  }

  function forPath(v) {
    if (v == null) return v;
    if (typeof v === 'string') return v;
    var p = path();
    if (v[p] !== undefined) return v[p];
    if (p === 'top' && v.gut !== undefined) return v.gut;
    if (v.all !== undefined) return v.all;
    return v.prep;
  }

  function visibleOptions(screen) {
    var p = path();
    return screen.options.filter(function (o) { return !o.paths || o.paths.indexOf(p) !== -1; });
  }

  /* ----------------------------------------------------------
     Classifier
     ---------------------------------------------------------- */
  function classify(a) {
    var p = path();
    if (a.concern === 'curious' || a.desire === 'bottom-more') return 'first-timer';
    if (p === 'prep' && a.routine === 'hose') return 'over-doucher';
    if (p === 'prep' && (a.burden === 'avoid' || a.cost === 'often' || a.desire === 'ready')) return 'spontaneity-seeker';
    if (a.tried && a.tried !== 'never') return 'switcher';
    var lowSymptom = (a.poops === 'clean' && a.trust === 'confident' && a.cost !== 'often');
    if (lowSymptom) return 'optimizer';
    if (p === 'prep') return 'ritual-prepper';
    if (p === 'top' && a.poops === 'clean') return 'optimizer';
    return 'gut-first';
  }

  /* ----------------------------------------------------------
     Tracking (defensive: every integration may be absent)
     ---------------------------------------------------------- */
  function fb(eventName) {
    try { if (window.fbq) window.fbq('trackCustom', eventName); } catch (e) { /* no-op */ }
  }

  // No email gate: the event still reaches Klaviyo for visitors it already
  // knows from a past signup (cookied profiles); new visitors stay anonymous.
  function klaviyoTrack(persona) {
    try {
      var k = window.klaviyo || window._learnq;
      if (!k) return false;
      var props = { persona: persona, quiz_path: path() };
      Object.keys(state.answers).forEach(function (key) { props['answer_' + key] = state.answers[key]; });
      if (typeof k.track === 'function') {
        k.track('Completed Gut Check', props);
      } else if (typeof k.push === 'function') {
        k.push(['track', 'Completed Gut Check', props]);
      } else {
        return false;
      }
      return true;
    } catch (e) { return false; }
  }

  /* ----------------------------------------------------------
     Rendering
     ---------------------------------------------------------- */
  var progressFill, backBtn, screenHost;
  var pendingTimeouts = [];
  var pendingInterval = null;
  var screenLocked = false;
  var proofPreloaded = false;

  function later(fn, ms) { pendingTimeouts.push(setTimeout(fn, ms)); }
  function clearPending() {
    pendingTimeouts.forEach(function (t) { clearTimeout(t); });
    pendingTimeouts = [];
    if (pendingInterval !== null) { clearInterval(pendingInterval); pendingInterval = null; }
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function updateChrome() {
    var s = SCREENS[state.idx];
    // Index-based progress so the bar moves on every screen.
    var pct = Math.round((state.idx / (SCREENS.length - 1)) * 100);
    progressFill.style.width = pct + '%';
    var canGoBack = state.idx > 0 && s.type !== 'result' && s.type !== 'analyzing';
    backBtn.classList.toggle('is-visible', canGoBack);
  }

  function go(idx) {
    state.idx = Math.max(0, Math.min(idx, SCREENS.length - 1));
    persist();
    render();
    try { window.scrollTo({ top: 0, behavior: 'instant' }); } catch (e) { window.scrollTo(0, 0); }
  }

  function next() { go(state.idx + 1); }

  function render() {
    clearPending();
    screenLocked = false;
    var s = SCREENS[state.idx];
    screenHost.textContent = '';
    var wrap = el('div', 'pq-screen');
    if (s.type === 'single') renderQuestion(wrap, s);
    else if (s.type === 'proof') renderProof(wrap, s);
    else if (s.type === 'analyzing') renderAnalyzing(wrap, s);
    else if (s.type === 'result') renderResult(wrap);
    screenHost.appendChild(wrap);
    updateChrome();
  }

  function renderQuestion(wrap, s) {
    if (s.opening) {
      wrap.classList.add('pq-screen--opening');
      wrap.appendChild(el('p', 'pq-kicker', s.kicker));
      wrap.appendChild(el('h1', 'pq-heading', s.heading));
      var check = el('p', 'pq-check');
      check.appendChild(el('span', 'pq-check__mark', '✓'));
      check.appendChild(el('span', null, s.check));
      wrap.appendChild(check);
      if (s.skip) {
        var skip = el('a', 'pq-skip', s.skip);
        skip.href = DEST_URL;
        wrap.appendChild(skip);
      }
      if (s.review) {
        wrap.appendChild(buildReview(s.review));
      }
      wrap.appendChild(el('h2', 'pq-prompt', forPath(s.prompt)));
    } else {
      wrap.appendChild(el('h1', 'pq-prompt', forPath(s.prompt)));
    }
    var opts = el('div', 'pq-opts');
    visibleOptions(s).forEach(function (o) {
      var b = el('button', 'pq-opt');
      b.type = 'button';
      if (o.emoji) b.appendChild(el('span', 'pq-opt__emoji', o.emoji));
      b.appendChild(el('span', null, forPath(o.label)));
      if (state.answers[s.id] === o.value) b.classList.add('is-picked');
      b.addEventListener('click', function () {
        if (screenLocked) return;
        screenLocked = true;
        if (s.opening && state.answers[s.id] == null) fb('GutCheckStart');
        if (s.id === 'position' && state.answers.position != null && state.answers.position !== o.value) {
          // Path change invalidates every answer given under the old path.
          state.answers = {};
        }
        state.answers[s.id] = o.value;
        b.classList.add('is-picked');
        if (PROOF_IMG && !proofPreloaded && path() !== 'gut') {
          proofPreloaded = true;
          new Image().src = PROOF_IMG;
        }
        later(next, 180);
      });
      opts.appendChild(b);
    });
    wrap.appendChild(opts);
    if (s.why) {
      var d = el('details', 'pq-why');
      var sum = el('summary', null, s.why.heading);
      d.appendChild(sum);
      d.appendChild(el('p', null, forPath(s.why.body)));
      wrap.appendChild(d);
    }
  }

  function buildReview(review) {
    var quote = REVIEW_QUOTE || review.quote;
    var author = REVIEW_AUTHOR || review.author;
    if (L10N && REVIEW_QUOTE) {
      // The customizer review is English; use its translation when Liquid
      // supplied one, otherwise the built-in (translated) review.
      if (L10N.review_custom_quote) {
        quote = L10N.review_custom_quote;
        author = L10N.review_custom_author || author;
      } else {
        quote = review.quote;
        author = review.author;
      }
    }
    var r = el('figure', 'pq-review');
    var row = el('div', 'pq-review__row');
    if (REVIEW_PHOTO) {
      var avatar = el('img', 'pq-review__avatar');
      avatar.src = REVIEW_PHOTO;
      avatar.alt = author;
      row.appendChild(avatar);
    }
    var body = el('div', 'pq-review__body');
    body.appendChild(el('blockquote', 'pq-review__quote', quote));
    var cap = el('figcaption', 'pq-review__author');
    cap.appendChild(el('span', null, author));
    cap.appendChild(el('span', 'pq-review__badge', '✓'));
    body.appendChild(cap);
    row.appendChild(body);
    r.appendChild(row);
    return r;
  }

  function renderProof(wrap, s) {
    wrap.classList.add('pq-inter');
    wrap.appendChild(el('h1', 'pq-heading', s.heading));
    // The proof creative is sex-coded; the gut-only path never opted into
    // that register, so it stays imageless there.
    if (PROOF_IMG && path() !== 'gut') {
      var img = el('img', 'pq-proof-img');
      img.src = PROOF_IMG;
      img.alt = T('ui.proof_alt', 'Peachy Clean: clean hole, dirty intentions');
      wrap.appendChild(img);
    }
    wrap.appendChild(el('p', 'pq-sub', forPath(s.sub)));
    var btn = el('button', 'pq-cta', s.cta);
    btn.type = 'button';
    btn.addEventListener('click', next);
    wrap.appendChild(btn);
  }

  var STEP_ICONS = {
    chart: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v18h18"/><path d="M6 15l4-5 3 3 5-7"/></svg>',
    loop: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 2l4 4-4 4"/><path d="M3 11V9a4 4 0 014-4h14"/><path d="M7 22l-4-4 4-4"/><path d="M21 13v2a4 4 0 01-4 4H3"/></svg>',
    gauge: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6"/></svg>',
    sparkle: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"><path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z"/><path d="M19 15l.7 1.8L21.5 17.5l-1.8.7L19 20l-.7-1.8-1.8-.7 1.8-.7L19 15z"/></svg>'
  };

  function renderAnalyzing(wrap, s) {
    wrap.classList.add('pq-analyzing-screen');
    wrap.appendChild(el('h1', 'pq-heading', s.heading));
    wrap.appendChild(el('p', 'pq-sub', s.sub));
    var list = el('div', 'pq-steps');
    var rows = s.steps.map(function (step) {
      var row = el('div', 'pq-step');
      var icon = el('span', 'pq-step__icon');
      icon.innerHTML = STEP_ICONS[step.icon] || '';
      row.appendChild(icon);
      row.appendChild(el('span', 'pq-step__label', step.label));
      var status = el('span', 'pq-step__status');
      status.appendChild(el('span', 'pq-step__dot'));
      status.appendChild(el('span', 'pq-step__spinner'));
      status.appendChild(el('span', 'pq-step__check', '✓'));
      row.appendChild(status);
      list.appendChild(row);
      return row;
    });
    wrap.appendChild(list);
    if (s.footnote) wrap.appendChild(el('p', 'pq-analyzing-note', s.footnote));

    var reduced = false;
    try { reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { /* default */ }
    if (reduced) {
      rows.forEach(function (row) { row.classList.add('is-done'); });
      later(next, 500);
      return;
    }
    var STEP_MS = 1150;
    rows.forEach(function (row, i) {
      later(function () { row.classList.add('is-active'); }, i * STEP_MS);
      later(function () {
        row.classList.remove('is-active');
        row.classList.add('is-done');
      }, (i + 1) * STEP_MS);
    });
    later(next, rows.length * STEP_MS + 450);
  }

  function renderResult(wrap) {
    if (!personaPreview && !state.persona) {
      state.persona = classify(state.answers);
      state.kSynced = klaviyoTrack(state.persona);
      persist();
      fb('GutCheckComplete');
    } else if (!personaPreview && !state.kSynced) {
      state.kSynced = klaviyoTrack(state.persona);
      persist();
    }
    var persona = personaPreview || state.persona || classify(state.answers);
    var P = PERSONAS[persona] || PERSONAS[classify(state.answers)] || PERSONAS['gut-first'];
    wrap.classList.add('pq-result');

    wrap.appendChild(el('p', 'pq-kicker', T('ui.result_prefix', 'Your result:') + ' ' + P.title));
    wrap.appendChild(el('h1', 'pq-heading', P.headline));

    var chips = el('div', 'pq-chips');
    var added = 0;
    Object.keys(state.answers).forEach(function (qid) {
      var entry = CHIP_LABELS[qid + ':' + state.answers[qid]];
      if (!entry || added >= 4) return;
      var label = forPath(entry);
      if (!label) return;
      chips.appendChild(el('span', 'pq-chip', label));
      added++;
    });
    if (added) {
      wrap.appendChild(el('p', 'pq-sub', T('ui.based_on', 'Based on what you told us:')));
      wrap.appendChild(chips);
    }

    var story = el('div', 'pq-story');
    P.beats.forEach(function (b) {
      var card = el('div', 'pq-beat');
      card.appendChild(el('h3', null, b.h));
      card.appendChild(el('p', null, b.p));
      story.appendChild(card);
    });
    wrap.appendChild(story);

    var tl = el('div', 'pq-timeline');
    tl.appendChild(el('h3', null, T('ui.what_to_expect', 'What to expect')));
    tl.appendChild(el('p', 'pq-timeline__lead', T('ui.timeline_lead', 'Most guys see results within 48 hours.')));
    TIMELINE.forEach(function (row) {
      var r = el('div', 'pq-tl-row');
      r.appendChild(el('span', 'pq-tl-when', row.when));
      r.appendChild(el('p', 'pq-tl-what', row.what));
      tl.appendChild(r);
    });
    wrap.appendChild(tl);

    var plan = el('div', 'pq-plan');
    plan.appendChild(el('p', 'pq-plan__eyebrow', T('ui.plan_eyebrow', 'Your protocol')));
    if (PRODUCT_IMG) {
      var pimg = el('img', 'pq-plan__img');
      pimg.src = PRODUCT_IMG;
      pimg.alt = T('ui.product_name', 'Peachy Clean Pre+Probiotic Fiber Gummies');
      plan.appendChild(pimg);
    }
    plan.appendChild(el('h3', null, T('ui.product_name', 'Peachy Clean Pre+Probiotic Fiber Gummies')));
    plan.appendChild(el('p', null, T('ui.plan_body', 'Two gummies a day. Prebiotic fiber plus a spore-forming probiotic, built to feed your butyrate producers.')));
    plan.appendChild(el('span', 'pq-plan__offer', T('ui.offer', 'Up to ' + OFFER_PCT + '% off is live right now')));
    var g = el('p', 'pq-guarantee');
    g.appendChild(el('span', null, '✓'));
    g.appendChild(el('span', null, T('ui.guarantee', 'Your 60-day money-back guarantee is activated')));
    plan.appendChild(g);
    wrap.appendChild(plan);

    var cta = el('button', 'pq-cta', T('ui.cta', 'See my Peachy Clean offer'));
    cta.type = 'button';
    cta.style.marginTop = '18px';
    cta.addEventListener('click', function () {
      var url;
      try {
        url = new URL(DEST_URL, location.origin);
        var params = new URLSearchParams(location.search);
        params.forEach(function (vv, kk) {
          if (kk === 'quiz_reset' || kk === 'quiz_persona_preview') return;
          url.searchParams.set(kk, vv);
        });
        url.searchParams.set('quiz_persona', persona);
        url = url.toString();
      } catch (e) {
        url = DEST_URL;
      }
      location.href = url;
    });
    wrap.appendChild(cta);
  }

  /* ----------------------------------------------------------
     Boot
     ---------------------------------------------------------- */
  mount.classList.add('pq-root');
  var topbar = el('div', 'pq-topbar');
  var row = el('div', 'pq-topbar__row');
  backBtn = el('button', 'pq-back', T('ui.back', '← Back'));
  backBtn.type = 'button';
  backBtn.addEventListener('click', function () {
    var target = state.idx - 1;
    // Skip back over auto-advancing screens.
    while (target > 0 && SCREENS[target].type === 'analyzing') target--;
    go(target);
  });
  var logo = el('a', 'pq-logo');
  logo.href = T('_root', '/');
  logo.setAttribute('aria-label', T('ui.home_aria', 'Peachy Men home'));
  if (LOGO_URL) {
    var logoImg = el('img', 'pq-logo__img');
    logoImg.src = LOGO_URL;
    logoImg.alt = 'Peachy Men';
    logo.appendChild(logoImg);
  } else {
    logo.textContent = 'peachy';
  }
  row.appendChild(backBtn);
  row.appendChild(logo);
  row.appendChild(el('span', 'pq-topbar__spacer', ''));
  topbar.appendChild(row);
  var progress = el('div', 'pq-progress');
  progressFill = el('div', 'pq-progress__fill');
  progress.appendChild(progressFill);
  topbar.appendChild(progress);
  mount.appendChild(topbar);
  screenHost = el('div');
  screenHost.style.cssText = 'width:100%;display:flex;flex-direction:column;align-items:center;flex:1;';
  mount.appendChild(screenHost);

  if (personaPreview) {
    state.idx = SCREENS.length - 1;
  } else if (SCREENS[state.idx] && SCREENS[state.idx].type === 'analyzing') {
    // Never resume onto an auto-advancing screen.
    state.idx++;
  }
  render();
})();
